<!--@format-->
<template>
    <div class="list-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ list.id ? 'Edit List Name' : 'Create List' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="loading" />
                <div v-else>
                    <div v-if="!list.id" class="intro-container">
                        <h4>No list to upload? No problem!</h4>
                        <p>
                            Just give us a name for your new list and start adding contacts right
                            away.
                        </p>
                    </div>
                    <div class="section">
                        <div class="section-container">
                            <div class="input-container">
                                <app-input
                                    v-model="list.name"
                                    :isValid="list.isValid"
                                    :label="'What is the name of your list?'"
                                    :error="'Name is required *'"
                                />
                            </div>
                            <div class="input-container">
                                <span class="section-title">Lead List?</span>
                                <div class="checkbox-section">
                                    <app-checkbox v-model="list.isMarketable" />
                                    <label class="marketable-text">
                                        Would you consider the contacts in this list as marketing
                                        qualified leads?
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget()"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="!list.id"
                        class="create-another-btn"
                        @click="createAnotherList()"
                    >
                        Create Another List
                    </app-button>
                    <app-button class="save-btn" @click="saveList()"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppInput = () => import('@/components/common/app-input');
const AppButton = () => import('@/components/common/app-button');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'list-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'app-button': AppButton,
        'app-checkbox': AppCheckbox,
        'loading-svg': LoadingSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            list: {
                id: this.widget.listId,
                name: null,
                isValid: true,
                isMarketable: false,
            },
            createMultipleLists: false,
            loading: false,
        };
    },

    created() {
        if (this.list.id) {
            this.getData();
        }
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        createList() {
            this.list.isValid = !!this.list.name;

            if (this.list.isValid) {
                this.loading = true;

                var request = {
                    clientId: this.widget.clientId,
                    name: this.list.name,
                    isMarketable: this.list.isMarketable,
                };

                this.$api
                    .post('/api/Lists', request)
                    .then((response) => {
                        EventBus.$emit('list:update');
                        var notification = {
                            iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                            isIconSvg: true,
                            message: `Successfully created ${this.list.name}`,
                            actionLabel: 'See list',
                            action: () => {
                                this.$router.push(`/Lists/${response.data.Id}`);
                                EventBus.$emit('close-notification', this.notification);
                                this.closeWidget();
                            },
                        };

                        EventBus.$emit('show-notification', notification);

                        if (this.createMultipleLists) {
                            this.list.name = '';
                            this.createMultipleLists = false;
                        } else {
                            this.closeWidget();
                        }
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'There was an error creating your list',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },

        editList() {
            this.loading = true;

            var changes = [
                {
                    name: 'Name',
                    value: this.list.name,
                },
                {
                    name: 'IsMarketable',
                    value: this.list.isMarketable,
                },
            ];

            this.$api
                .patch(`/api/Lists/${this.list.id}`, changes)
                .then(() => {
                    EventBus.$emit('list:update');
                    this.$helpers.showSuccessNotification('Your changes have been saved.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('There was an error updating your list', {
                        error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        createAnotherList() {
            this.createMultipleLists = true;
            this.createList();
        },

        saveList() {
            if (this.list.id) {
                this.editList();
            } else {
                this.createList();
            }
        },

        getData() {
            this.loading = true;

            var filterParam = `$filter=Id eq ${this.list.id}`;

            var query = `/service/ListDetails?${filterParam}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.list.name = response.data.value[0].Name;
                    this.list.isMarketable = response.data.value[0].IsMarketable;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your lists.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style>
.list-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.list-widget .intro-container {
    margin-bottom: 30px;
}

.list-widget .content-container {
    height: 100%;
}

.list-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid rgba(202, 198, 203, 1);
    padding: 0px 30px;
}

.list-widget .input-container {
    margin-bottom: 20px;
}

.list-widget .checkbox-section {
    display: flex;
}

.list-widget .checkbox-section .marketable-text {
    font-weight: normal;
    padding: 0px 10px;
}

.list-widget .section-title {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
}

.list-widget .widget-footer .action-container {
    display: flex;
}

.list-widget .widget-footer .action-container .save-btn {
    margin-left: 15px;
    padding: 0px 45px;
}

.list-widget .widget-footer .action-container .create-another-btn {
    background: transparent;
    color: rgba(24, 174, 255, 1);
    border: 1px solid rgba(24, 174, 255, 1);
}
</style>
